/**
 * Safe HTML parser that handles nested buttons and other HTML parsing quirks.
 * Browsers automatically close certain tags when they encounter invalid nesting,
 * which can break the intended DOM structure. This parser pre-processes the HTML
 * to handle such cases and automatically restores the original structure.
 */

import { restoreOriginalDocument } from "./restoreOriginalHtml";

// Unique markers for replaced elements
const REPLACEMENT_MARKERS = {
  NESTED_BUTTON: "data-safe-parser-nested-button",
  NESTED_FORM: "data-safe-parser-nested-form",
  NESTED_ANCHOR: "data-safe-parser-nested-anchor",
  INVALID_TABLE: "data-safe-parser-invalid-table",
  INVALID_OPTION: "data-safe-parser-invalid-option",
  BLOCK_IN_P: "data-safe-parser-block-in-p",
  INVALID_HEAD_ELEMENT: "data-safe-parser-invalid-head-element",
  BUTTON_IN_HEADER: "data-safe-parser-button-in-header",
  NESTED_HEADER: "data-safe-parser-nested-header",
} as const;

/**
 * Pre-processes HTML to fix common parsing issues that break DOM structure
 */
function preprocessHtml(html: string): string {
  let processedHtml = html;

  // 1. Fix nested headers FIRST (headers cannot contain other headers)
  processedHtml = fixNestedHeaders(processedHtml);

  // 2. Fix nested buttons (buttons cannot contain buttons)
  processedHtml = fixNestedButtons(processedHtml);

  // 3. Fix buttons and other interactive elements in text tags (headers and paragraphs)
  // This must be done early to prevent auto-closing of text tags
  processedHtml = fixInteractiveElementsInTextTags(processedHtml);

  // 4. Fix block elements inside <p> tags (p tags auto-close when block elements appear)
  // NOTE: This must be done BEFORE other fixes to ensure we catch the block elements
  processedHtml = fixBlockElementsInParagraphs(processedHtml);

  // 5. Fix nested forms (forms cannot contain forms)
  processedHtml = fixNestedForms(processedHtml);

  // 6. Fix nested anchor tags (anchors cannot contain anchors)
  processedHtml = fixNestedAnchors(processedHtml);

  // 7. Fix table-related nesting issues
  processedHtml = fixTableNesting(processedHtml);

  // 8. Fix option/select nesting issues
  processedHtml = fixSelectOptionNesting(processedHtml);

  // 9. Fix invalid elements in head
  processedHtml = fixInvalidHeadElements(processedHtml);

  return processedHtml;
}

/**
 * Fix nested headers (h1-h6 cannot contain other h1-h6)
 * Headers auto-close when they encounter other headers
 */
function fixNestedHeaders(html: string): string {
  const headerTags = ["h1", "h2", "h3", "h4", "h5", "h6"];
  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  // Track open headers using a stack approach
  const headerStack: { tag: string; start: number; attributes: string }[] = [];

  // Find all header opening and closing tags
  const headerRegex = new RegExp(
    `<(/?)(${headerTags.join("|")})(?=\\s|>)([^>]*)>`,
    "gi",
  );

  let match;
  const allMatches: {
    index: number;
    isClosing: boolean;
    tag: string;
    attributes: string;
    fullMatch: string;
  }[] = [];

  while ((match = headerRegex.exec(html)) !== null) {
    allMatches.push({
      index: match.index,
      isClosing: match[1] === "/",
      tag: match[2].toLowerCase(),
      attributes: match[3] || "",
      fullMatch: match[0],
    });
  }

  // Process matches to find nested headers
  allMatches.forEach((current, idx) => {
    if (!current.isClosing) {
      // Check if there's an open header on the stack
      if (headerStack.length > 0) {
        // This is a nested header - mark it for replacement
        replacements.push({
          start: current.index,
          end: current.index + current.fullMatch.length,
          replacement: `<span ${REPLACEMENT_MARKERS.NESTED_HEADER}="${
            current.tag
          }"${current.attributes ? ` ${current.attributes}` : ""}>`,
        });

        // Find the corresponding closing tag for this nested header
        let depth = 1;
        for (let i = idx + 1; i < allMatches.length; i++) {
          const next = allMatches[i];
          if (next.tag === current.tag) {
            if (!next.isClosing) {
              depth++;
            } else {
              depth--;
              if (depth === 0) {
                // Found the matching closing tag
                replacements.push({
                  start: next.index,
                  end: next.index + next.fullMatch.length,
                  replacement: `</span>`,
                });
                break;
              }
            }
          }
        }
      }

      // Add to stack
      headerStack.push({
        tag: current.tag,
        start: current.index,
        attributes: current.attributes,
      });
    } else {
      // Remove from stack if it matches
      let lastIdx = -1;
      for (let i = headerStack.length - 1; i >= 0; i--) {
        if (headerStack[i].tag === current.tag) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx !== -1) {
        headerStack.splice(lastIdx, 1);
      }
    }
  });

  return applyReplacements(html, replacements);
}

/**
 * Fix interactive elements (button, input, select, textarea) inside text tags (h1-h6, p)
 * These elements cause browsers to auto-close the text tags
 */
function fixInteractiveElementsInTextTags(html: string): string {
  // Text tags that auto-close when they encounter interactive elements
  const textTags = ["h1", "h2", "h3", "h4", "h5", "h6", "p"];

  // Interactive elements that cause text tags to auto-close
  const interactiveElements = ["button", "input", "select", "textarea", "form"];

  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  // For each text tag type
  textTags.forEach((textTag) => {
    // Create regex to find all instances of this text tag
    const textTagRegex = new RegExp(
      `<${textTag}(?=\\s|>)([^>]*)>([\\s\\S]*?)<\\/${textTag}>`,
      "gi",
    );

    let textMatch;
    while ((textMatch = textTagRegex.exec(html)) !== null) {
      const textTagStart = textMatch.index;
      const textTagContent = textMatch[2];
      const textTagContentStart =
        textTagStart + textMatch[0].indexOf(textTagContent);

      // Check if this text tag contains interactive elements
      interactiveElements.forEach((interactiveElement) => {
        const interactiveRegex = new RegExp(
          `<${interactiveElement}(?=\\s|>)([^>]*)>`,
          "gi",
        );

        let interactiveMatch;
        while (
          (interactiveMatch = interactiveRegex.exec(textTagContent)) !== null
        ) {
          const attributes = interactiveMatch[1] || "";
          const absoluteStart = textTagContentStart + interactiveMatch.index;

          // Replace the interactive element's opening tag with a span
          replacements.push({
            start: absoluteStart,
            end: absoluteStart + interactiveMatch[0].length,
            replacement: `<span ${
              REPLACEMENT_MARKERS.BUTTON_IN_HEADER
            }="${interactiveElement}"${attributes ? ` ${attributes}` : ""}>`,
          });

          // Find and replace the closing tag
          const closingTagRegex = new RegExp(
            `<\\/${interactiveElement}>`,
            "gi",
          );
          closingTagRegex.lastIndex = interactiveMatch.index;
          const closingMatch = closingTagRegex.exec(textTagContent);

          if (closingMatch) {
            const absoluteClosingStart =
              textTagContentStart + closingMatch.index;
            replacements.push({
              start: absoluteClosingStart,
              end: absoluteClosingStart + closingMatch[0].length,
              replacement: "</span>",
            });
          }
        }
      });
    }
  });

  return applyReplacements(html, replacements);
}

/**
 * Fix nested buttons by converting inner buttons to divs
 */
function fixNestedButtons(html: string): string {
  const buttonStack: { start: number; tagMatch: string }[] = [];
  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  const buttonRegex = /<button(?=\s|>)([^>]*)>|<\/button>/gi;
  let match;

  while ((match = buttonRegex.exec(html)) !== null) {
    const tag = match[0];
    const isClosing = tag.startsWith("</");

    if (!isClosing) {
      if (buttonStack.length > 0) {
        const attributes = match[1] || "";
        const replacement = `<div ${REPLACEMENT_MARKERS.NESTED_BUTTON}="true"${attributes}>`;
        replacements.push({
          start: match.index,
          end: match.index + tag.length,
          replacement,
        });
      }
      buttonStack.push({ start: match.index, tagMatch: tag });
    } else {
      if (buttonStack.length > 1) {
        replacements.push({
          start: match.index,
          end: match.index + tag.length,
          replacement: "</div>",
        });
      }
      if (buttonStack.length > 0) {
        buttonStack.pop();
      }
    }
  }

  return applyReplacements(html, replacements);
}

/**
 * Fix block elements inside paragraphs by converting them to spans
 */
function fixBlockElementsInParagraphs(html: string): string {
  // Block elements that cause <p> to auto-close
  const blockElements = [
    "div",
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "table",
    "form",
    "article",
    "section",
    "nav",
    "aside",
    "header",
    "footer",
    "main",
    "blockquote",
    "pre",
    "hr",
    "address",
  ];

  // Pre-compile regexes for better performance
  const blockRegexes = blockElements.map((element) => ({
    element,
    regex: new RegExp(
      `<${element}(\\s[^>]*)?>([\\s\\S]*?)<\\/${element}>`,
      "gi",
    ),
  }));

  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  // Find all <p> tags and their content
  const pTagRegex = /<p(?=\s|>)([^>]*)>([\s\S]*?)<\/p>/gi;
  let pMatch;

  while ((pMatch = pTagRegex.exec(html)) !== null) {
    const pStart = pMatch.index;
    const pContent = pMatch[2];
    const pContentStart = pStart + pMatch[0].indexOf(pContent);

    // Check if this paragraph contains block elements
    blockRegexes.forEach(({ element: blockElement, regex: blockRegex }) => {
      // Reset regex state for each use
      blockRegex.lastIndex = 0;
      let blockMatch;

      while ((blockMatch = blockRegex.exec(pContent)) !== null) {
        const attributes = blockMatch[1] || "";
        const innerContent = blockMatch[2];
        const absoluteStart = pContentStart + blockMatch.index;

        // Replace opening tag with span
        replacements.push({
          start: absoluteStart,
          end: absoluteStart + blockMatch[0].indexOf(">") + 1,
          replacement: `<span ${REPLACEMENT_MARKERS.BLOCK_IN_P}="${blockElement}"${attributes}>`,
        });

        // Replace closing tag with span
        const closingTagStart =
          absoluteStart + blockMatch[0].lastIndexOf(`</${blockElement}>`);
        replacements.push({
          start: closingTagStart,
          end: closingTagStart + `</${blockElement}>`.length,
          replacement: "</span>",
        });
      }
    });
  }

  return applyReplacements(html, replacements);
}

/**
 * Fix nested forms by converting inner forms to divs
 */
function fixNestedForms(html: string): string {
  const formStack: number[] = [];
  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  const formRegex = /<form(?=\s|>)([^>]*)>|<\/form>/gi;
  let match;

  while ((match = formRegex.exec(html)) !== null) {
    const tag = match[0];
    const isClosing = tag.startsWith("</");

    if (!isClosing) {
      if (formStack.length > 0) {
        const attributes = match[1] || "";
        const replacement = `<div ${REPLACEMENT_MARKERS.NESTED_FORM}="true"${attributes}>`;
        replacements.push({
          start: match.index,
          end: match.index + tag.length,
          replacement,
        });
      }
      formStack.push(match.index);
    } else {
      if (formStack.length > 1) {
        replacements.push({
          start: match.index,
          end: match.index + tag.length,
          replacement: "</div>",
        });
      }
      if (formStack.length > 0) {
        formStack.pop();
      }
    }
  }

  return applyReplacements(html, replacements);
}

/**
 * Fix nested anchor tags by converting inner anchors to spans
 */
function fixNestedAnchors(html: string): string {
  const anchorStack: number[] = [];
  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  const anchorRegex = /<a(?=\s|>)([^>]*)>|<\/a>/gi;
  let match;

  while ((match = anchorRegex.exec(html)) !== null) {
    const tag = match[0];
    const isClosing = tag.startsWith("</");

    if (!isClosing) {
      if (anchorStack.length > 0) {
        const attributes = match[1] || "";
        const replacement = `<span ${REPLACEMENT_MARKERS.NESTED_ANCHOR}="true"${attributes}>`;
        replacements.push({
          start: match.index,
          end: match.index + tag.length,
          replacement,
        });
      }
      anchorStack.push(match.index);
    } else {
      if (anchorStack.length > 1) {
        replacements.push({
          start: match.index,
          end: match.index + tag.length,
          replacement: "</span>",
        });
      }
      if (anchorStack.length > 0) {
        anchorStack.pop();
      }
    }
  }

  return applyReplacements(html, replacements);
}

/**
 * Fix table nesting issues - ensure tr, td, th are only in proper parents
 */
function fixTableNesting(html: string): string {
  let processedHtml = html;

  // First pass: Convert improperly nested TR elements and track their positions
  const trReplacements: {
    start: number;
    end: number;
    replacement: string;
    innerStart: number;
    innerEnd: number;
  }[] = [];
  const trRegex = /<tr(?=\s|>)([^>]*)>/gi;
  let match;

  while ((match = trRegex.exec(processedHtml)) !== null) {
    const position = match.index;
    const beforeContent = processedHtml.substring(0, position);

    // Check if tr is properly nested
    const isProperlyNested =
      /<(table|thead|tbody|tfoot)[^>]*>(?![\s\S]*<\/\1>)[\s\S]*$/.test(
        beforeContent,
      );

    if (!isProperlyNested) {
      const attributes = match[1] || "";
      const openTag = match[0];

      // Find the closing </tr> tag
      const closingRegex = /<\/tr>/gi;
      closingRegex.lastIndex = match.index;
      const closingMatch = closingRegex.exec(processedHtml);

      if (closingMatch) {
        // Track this TR conversion
        trReplacements.push({
          start: match.index,
          end: match.index + openTag.length,
          replacement: `<div ${REPLACEMENT_MARKERS.INVALID_TABLE}="tr"${attributes}>`,
          innerStart: match.index + openTag.length,
          innerEnd: closingMatch.index,
        });

        // Add closing tag replacement
        trReplacements.push({
          start: closingMatch.index,
          end: closingMatch.index + closingMatch[0].length,
          replacement: "</div>",
          innerStart: -1,
          innerEnd: -1,
        });
      }
    }
  }

  // Apply TR replacements
  trReplacements.sort((a, b) => b.start - a.start);
  trReplacements.forEach(({ start, end, replacement }) => {
    processedHtml =
      processedHtml.slice(0, start) + replacement + processedHtml.slice(end);
  });

  // Second pass: Convert TD/TH elements that are now inside converted TR divs
  const tdThReplacements: {
    start: number;
    end: number;
    replacement: string;
  }[] = [];
  const tdThRegex = /<(td|th)(?=\s|>)([^>]*)>/gi;

  while ((match = tdThRegex.exec(processedHtml)) !== null) {
    const tag = match[1].toLowerCase();
    const position = match.index;
    const beforeContent = processedHtml.substring(0, position);

    // Check if td/th is inside a converted tr (now a div with marker)
    const isInConvertedTr =
      /<div[^>]*data-safe-parser-invalid-table="tr"[^>]*>(?![\s\S]*<\/div>)[\s\S]*$/.test(
        beforeContent,
      );

    // Check if td/th is properly nested in a real tr
    const isProperlyNested = /<tr[^>]*>(?![\s\S]*<\/tr>)[\s\S]*$/.test(
      beforeContent,
    );

    if (isInConvertedTr || !isProperlyNested) {
      const attributes = match[2] || "";
      const replacement = `<div ${REPLACEMENT_MARKERS.INVALID_TABLE}="${tag}"${attributes}>`;

      tdThReplacements.push({
        start: match.index,
        end: match.index + match[0].length,
        replacement,
      });

      // Also replace the closing tag
      const closingRegex = new RegExp(`<\\/${tag}>`, "gi");
      closingRegex.lastIndex = match.index;
      const closingMatch = closingRegex.exec(processedHtml);
      if (closingMatch) {
        tdThReplacements.push({
          start: closingMatch.index,
          end: closingMatch.index + closingMatch[0].length,
          replacement: "</div>",
        });
      }
    }
  }

  // Apply TD/TH replacements
  processedHtml = applyReplacements(processedHtml, tdThReplacements);

  return processedHtml;
}

/**
 * Fix select/option nesting - options can only be in select or optgroup
 */
function fixSelectOptionNesting(html: string): string {
  const replacements: { start: number; end: number; replacement: string }[] =
    [];

  const optionRegex = /<option(?=\s|>)([^>]*)>/gi;
  let match;

  while ((match = optionRegex.exec(html)) !== null) {
    const position = match.index;
    const beforeContent = html.substring(0, position);

    // Check if option is properly nested in select or optgroup
    const isProperlyNested =
      /<(select|optgroup)[^>]*>(?![\s\S]*<\/\1>)[\s\S]*$/.test(beforeContent);

    if (!isProperlyNested) {
      const attributes = match[1];
      const replacement = `<div ${REPLACEMENT_MARKERS.INVALID_OPTION}="true"${attributes}>`;
      replacements.push({
        start: match.index,
        end: match.index + match[0].length,
        replacement,
      });

      // Replace closing tag
      const closingRegex = /<\/option>/gi;
      closingRegex.lastIndex = match.index;
      const closingMatch = closingRegex.exec(html);
      if (closingMatch) {
        replacements.push({
          start: closingMatch.index,
          end: closingMatch.index + closingMatch[0].length,
          replacement: "</div>",
        });
      }
    }
  }

  return applyReplacements(html, replacements);
}

/**
 * Fix invalid elements in head by converting them to special script tags with encoded data
 */
function fixInvalidHeadElements(html: string): string {
  // Elements that are not allowed in head
  const invalidInHead = [
    "div",
    "span",
    "p",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "table",
    "tr",
    "td",
    "th",
    "form",
    "input",
    "button",
    "a",
    "img",
    "video",
    "audio",
    "canvas",
    "svg",
    "article",
    "section",
    "nav",
    "aside",
    "header",
    "footer",
    "main",
  ];

  // Pre-compile regexes for better performance
  const invalidRegexes = invalidInHead.map((tagName) => ({
    tagName,
    regex: new RegExp(
      `<${tagName}(\\s[^>]*)?>([\\s\\S]*?)<\\/${tagName}>`,
      "gi",
    ),
  }));

  const replacements: { start: number; end: number; replacement: string }[] =
    [];
  const invalidElements: string[] = [];

  // Find head section
  const headMatch = html.match(/<head(?:\s[^>]*)?>[\s\S]*?<\/head>/i);
  if (!headMatch) return html;

  const headStart = html.indexOf(headMatch[0]);
  const headContent = headMatch[0];
  const headContentStart = headStart + headContent.indexOf(">") + 1;
  const headContentEnd = headStart + headContent.lastIndexOf("</head>");

  // Find invalid elements within head
  invalidRegexes.forEach(({ tagName, regex: tagRegex }) => {
    // Reset regex state for each use
    tagRegex.lastIndex = 0;
    let match;

    while ((match = tagRegex.exec(headContent)) !== null) {
      const absoluteStart = headStart + match.index;

      // Only process if it's within the head content (not in the head tag itself)
      if (absoluteStart >= headContentStart && absoluteStart < headContentEnd) {
        // Encode the entire element for restoration
        const elementHtml = match[0];
        const encodedElement = btoa(elementHtml);
        invalidElements.push(encodedElement);

        // Replace with a placeholder script that won't be moved by the browser
        const placeholder = `<script type="text/x-safe-parser-invalid-head" data-element-index="${
          invalidElements.length - 1
        }"></script>`;

        replacements.push({
          start: absoluteStart,
          end: absoluteStart + match[0].length,
          replacement: placeholder,
        });
      }
    }
  });

  let result = applyReplacements(html, replacements);

  // Add a script in the head to store all invalid elements
  if (invalidElements.length > 0) {
    const storageScript = `<script type="text/x-safe-parser-storage">${JSON.stringify(
      invalidElements,
    )}</script>`;
    result = result.replace("</head>", `${storageScript}</head>`);
  }

  return result;
}

/**
 * Apply replacements to HTML string
 */
function applyReplacements(
  html: string,
  replacements: { start: number; end: number; replacement: string }[],
): string {
  // Sort replacements in reverse order to maintain positions
  replacements.sort((a, b) => b.start - a.start);

  let result = html;
  replacements.forEach(({ start, end, replacement }) => {
    result = result.slice(0, start) + replacement + result.slice(end);
  });

  return result;
}

/**
 * Creates a new DOMParser instance
 */
function createParser(): DOMParser {
  return new DOMParser();
}

/**
 * Safely parses HTML string into a Document, handling various parsing issues
 * @param html - The HTML string to parse
 * @param type - The MIME type (default: "text/html")
 * @returns The parsed Document with original structure restored
 */
export function safeParseDocumentFromString(
  html: string,
  type: DOMParserSupportedType = "text/html",
): Document {
  // Pre-process HTML to handle parsing issues
  const processedHtml = preprocessHtml(html);

  // Parse the processed HTML
  const parser = createParser();
  const doc = parser.parseFromString(processedHtml, type);

  // Check for parser errors (only applies to XML parsing)
  if (type !== "text/html") {
    const parserError = doc.querySelector("parsererror");
    if (parserError) {
      console.error("Parser error:", parserError.textContent);
    }
  }

  // Restore the original structure directly on the document
  restoreOriginalDocument(doc);

  return doc;
}
